import AddCircleIcon from '@mui/icons-material/AddCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import { Box, Collapse, Paper, Typography } from '@mui/material'
import React, { useEffect, useMemo, useState } from 'react'
import Modal from '../../../common/modal'

const STATUS_CONFIG = {
  B: {
    fields: [
      ['Booking Date & Time', 'BookingDatetime'],
      ['Source', 'SourceCity'],
      ['Destination', 'DestinationCity'],
      ['Source Branch', 'SourceBranch'],
      ['Destination Branch', 'DestinationBranch'],
    ],
  },
  T: {
    fields: [
      ['Transfer Date & Time', 'BranchTransferDateTime'],
      ['Transfer To', 'TransferredToBranch'],
      ['Vehicle Loaded', 'BranchTransferVehicle'],
      ['Driver & Co-driver', 'BranchTransferDriverName'],
    ],
  },
  G: {
    fields: [
      ['Transfer Receive Date & Time', 'BranchTransferReceiveDateTime'],
      ['Received At', 'ReceivedAtBranch'],
      ['Vehicle Loaded', 'BranchTransferReceiveVehicle'],
      ['Driver & Co-driver', 'BranchTransferReceiveDriverName'],
    ],
  },
  D: {
    fields: [
      ['Dispatch Date & Time', 'DispatchDateTime'],
      ['Dispatch To', 'DispatchedToBranch'],
      ['Vehicle Loaded', 'DispatchedVehicle'],
      ['Driver & Co-driver', 'DispatchedDriverName'],
    ],
  },
  R: {
    fields: [
      ['Receive Date & Time', 'ReceivedDateTime'],
      ['Received At', 'ReceivedToBranch'],
      ['Vehicle Loaded', 'ReceivedVehicle'],
      ['Driver & Co-driver', 'ReceivedDriverName'],
    ],
  },
  O: {
    fields: [
      ['Assigned Date & Time', 'AssignedDateTime'],
      ['Assigned To', 'AssignedTo'],
      ['Vehicle Loaded', 'VehicleLoaded'],
      ['Assigned By', 'AssignedBy'],
    ],
  },
  OR: {
    fields: [
      ['Assigned Return Date', 'AssignedReturnDate'],
      ['Returned By', 'ReturnedBy'],
      ['Returned To', 'ReturnedTo'],
    ],
  },
  F: {
    fields: [
      ['Delivery Date & Time', 'DeliveredDateTime'],
      ['Delivered To', 'DeliveredTo'],
      ['Vehicle Loaded', 'DeliveryVehicleLoaded'],
      ['Delivered By', 'DeliveredBy'],
    ],
  },
  C: {
    fields: [
      ['Cancel Date & Time', 'RefundDatetime'],
      ['Cancel By', 'RefundBy'],
    ],
  },
}

const getScanKey = (scan = {}) => [scan?.EventType, scan?.BookingID, scan?.UnitID, scan?.ScannedTime, scan?.consginmentsubtypeid].join('|')

const LRStatusModel = ({ setOpenLrModal, LRTrackStatusData = [], DefaultCurrencyID = 1 }) => {
  const [expandedSections, setExpandedSections] = useState({})

  const modalStyle = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  }

  useEffect(() => {
    const firstExpandableSection = LRTrackStatusData.find((item) => item?.unitScans?.length > 0)?.Type

    setExpandedSections(firstExpandableSection ? { [firstExpandableSection]: false } : {})
  }, [LRTrackStatusData])

  const finalStatus = LRTrackStatusData[LRTrackStatusData.length - 1]?.TypeDet || ''

  const toggleSection = (type) => {
    setExpandedSections((prev) => ({
      ...prev,
      [type]: !prev[type],
    }))
  }

  const renderField = (label, value) => {
    if (!value) return null

    return (
      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '12px', mb: 0.5 }}>
        {label}: {value}
      </Typography>
    )
  }

  const renderHeader = (data) => (
    <Paper sx={{ border: '1px solid #2f55d4', p: 1.5, mb: 1, boxShadow: 'none' }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: '14px', color: '#2f55d4' }}>
        WayBill: {data?.LRNO}
      </Typography>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '14px', color: '#2faa58' }}>
        Status: {finalStatus}
      </Typography>
    </Paper>
  )

  const renderSection = (data, index) => {
    const type = data?.Type?.toUpperCase()
    const sectionConfig = STATUS_CONFIG[type]
    if (DefaultCurrencyID === 2 && type === 'G') {
      data.TypeDet = `SC Transfer Receive`
    }

    if (!sectionConfig) {
      return null
    }

    const hasUnitScans = data?.unitScans?.length > 0
    const isExpanded = Boolean(expandedSections[type])

    return (
      <React.Fragment key={`${type}-${index}`}>
        {index === 0 && renderHeader(data)}
        <Paper sx={{ border: '1px solid #2f55d4', mb: 1, overflow: 'hidden', boxShadow: 'none' }}>
          <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
            {hasUnitScans && (
              <Box
                sx={{
                  width: 52,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRight: '1px solid #2f55d4',
                }}
                onClick={() => toggleSection(type)}
              >
              {isExpanded ?<RemoveCircleIcon sx={{ color: '#2F89EB', fontSize: '30px' }} /> : <AddCircleIcon sx={{ color: '#2F89EB', fontSize: '30px' }}/>}
              </Box>
            )}
            <Box sx={{ flex: 1, p: 1.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '14px', mb: 0.75 }}>
                {data.TypeDet}:
              </Typography>
              {sectionConfig.fields.map(([label, field]) => (
                <React.Fragment key={field}>{renderField(label, data?.[field])}</React.Fragment>
              ))}
            </Box>
          </Box>

          {hasUnitScans && (
            <Collapse in={isExpanded}>
              <Box sx={{ display: 'flex', borderTop: '1px solid #d9d9d9' }}>
                <Box sx={{ width: 52, }} />
                <Box sx={{ flex: 1 }}>
                  {data.unitScans.map((scan, scanIndex) => (
                    <Box
                      key={getScanKey(scan)}
                      sx={{
                        px: 1.5,
                        py: 0.8,
                        borderTop: scanIndex === 0 ? 'none' : '1px solid #e6e6e6',
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '12px' }}>
                        Unit Scan {scanIndex + 1}:{' '}
                        <Box component="span" sx={{ fontWeight: 600 }}>
                          {scan?.ScannedTime}
                        </Box>
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Collapse>
          )}
        </Paper>
      </React.Fragment>
    )
  }

  return (
    <Modal
      headTxt="LR Status"
      width="min(560px, 92vw)"
      updateModal={() => setOpenLrModal(false)}
      modalClass="booking-popup"
      modalStyle={modalStyle}
    >
      <Box sx={{ p: 2, maxHeight: '80vh', overflowY: 'auto' }}>
        {LRTrackStatusData.length > 0 ? (
          LRTrackStatusData.map((item, index) => renderSection(item, index))
        ) : (
          <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center' }}>
            No tracking data available
          </Typography>
        )}
      </Box>
    </Modal>
  )
}

export default LRStatusModel
